// ---------------------------------------------------------------------------
// Serviço de Gamificação — Lura
// Responsabilidade única: verificar e desbloquear badges após eventos de leitura.
// Chamado pelo Frontend após uma transação de conclusão de livro.
// ---------------------------------------------------------------------------

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { UserProfile } from "../../../services/firebase/userService";
import { enqueueBadgeUnlocks, markBadgesCelebrated } from "../utils/badgeUnlockQueue";
import { watchMetaOverlayWhenAuthed } from "../../../lib/firebase/metaOverlay";
import { BADGE_LORE } from "../constants/badgeLore";

const FUNCTIONS_REGION = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION ?? "us-central1";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

/**
 * Degrau de dificuldade. Espelho de `BadgeMeta["rarity"]`
 * (`functions/src/shared/badgeCatalog.ts`) — as duas listas são comparadas por
 * `tests/unit/leagueBadgeCatalog.test.ts`.
 *
 * Reusa o mesmo vocabulário dos cosméticos (`CosmeticRarity`) de propósito: as
 * duas escadas convivem no perfil, e quem aprendeu "lendário é o topo" na Loja
 * tem de ler a mesma coisa nas medalhas.
 */
export type BadgeRarity = "common" | "rare" | "epic" | "legendary";

export interface BadgeDefinition {
  id: string;
  label: string;
  emoji: string;
  description: string;
  /**
   * A mesma condição dita para QUEM VISITA o perfil (flag da fileira pública).
   * `description` fala com o dono ("Você concluiu seu primeiro livro"), e no
   * perfil alheio o "você" aponta para a pessoa errada. Só existe onde a
   * `description` tem segunda pessoa; ausente = a `description` já serve.
   * Leia por `badgePublicDescription`, nunca cru.
   */
  publicDescription?: string;
  /**
   * Frase simbólica da secreta (`constants/badgeLore.ts`). Quando existe, é o
   * que todo mundo lê no lugar da receita; o dono vê a receita só como "Como
   * você ganhou". Leia por `badgePublicDescription`/`badgeShowcaseText`/
   * `badgeHowEarned`, nunca cru.
   */
  lore?: string;
  secret?: boolean;
  /** Obrigatório: sem isso badge nova entra muda no degrau mais baixo. */
  rarity: BadgeRarity;
}

// Stats mínimos necessários para avaliação de badges.
// Campos opcionais mantêm retrocompatibilidade com chamadores que não os fornecem.
export type BadgeCheckStats = {
  totalBooksRead: number;
  totalPagesRead: number;
  currentStreak: number;
  /**
   * Recorde vitalício de sequência (`user_stats.longestStreak`).
   *
   * É a régua das badges de ofensiva desde o BUG-219 — ver `bestStreakOf`.
   * Opcional porque doc anterior ao campo cai em 0, e aí quem responde é a
   * sequência viva.
   */
  longestStreak?: number;
  wallet?: { coins: number };
  // totalReads inclui releituras; uniqueBooksRead exclui — diferença detecta zona_conforto
  totalReads?: number;
  uniqueBooksRead?: number;
  /**
   * Livros concluídos DENTRO do ano corrente — só `meta_cumprida` usa.
   *
   * Ausente = não apurado, e a badge não é nem tentada. É o mesmo fail-closed
   * do gate server-side (`functions/src/books/badges.ts`): sem o dado, não se
   * concede — badge é one-way e o cron de higiene não revoga por condição.
   */
  booksReadThisYear?: number;
  /**
   * Resenhas escritas na vida — `user_stats.totalReviews`, mantido pelo trigger
   * `onBookReviewWritten` (+1 no create, −1 no delete).
   *
   * Ausente = doc anterior ao contador. Cai em 0, e as três badges de resenha
   * simplesmente não são tentadas — mesmo fail-closed de `booksReadThisYear`.
   * ⚠️ **Não há backfill**: resenha escrita antes do contador existir não
   * conta. Contar o passado exigiria varrer `book_reviews` inteiro, e o gate é
   * server-side de qualquer forma.
   */
  totalReviews?: number;
  /**
   * Resenhas com TEXTO (≥ 20 caracteres) — `user_stats.totalWrittenReviews`
   * (24/09, BACKLOG §0 item 11 F3). Régua de `primeira_resenha` e de
   * `critico_carreira`; ausente cai em 0 (fail-closed).
   */
  totalWrittenReviews?: number;
  /** Livros em Quero ler agora — `user_stats.wantToReadCount` (Tsundoku). */
  wantToReadCount?: number;
  /** Gêneros distintos lidos — `user_stats.genresRead` (Eclético, só barra). */
  genresReadCount?: number;
  /** Obras independentes distintas lidas — `user_stats.indieWorksRead` (Garimpo, só barra). */
  indieWorksReadCount?: number;
  /**
   * Minutos de foco RECOMPENSADOS acumulados (`user_stats.totalFocusMin`).
   *
   * São os minutos que o servidor pagou — já com o teto diário de 150 e o corte
   * de overrun do pomodoro aplicados —, não o relógio de parede. É de propósito:
   * cronômetro esquecido não vira conquista (a mesma medição que criou o
   * `MAX_POMODORO_OVERRUN_SEC` em 24/08).
   */
  totalFocusMin?: number;
};

// ---------------------------------------------------------------------------
// Catálogo de badges com regras de desbloqueio
// ---------------------------------------------------------------------------

interface BadgeRule extends BadgeDefinition {
  check: (stats: BadgeCheckStats, profile: UserProfile) => boolean;
  /**
   * Só o caminho de EVENTO (`checkAndUnlockBadges`, na conclusão do livro)
   * avalia esta regra; o gatilho passivo (`getEligibleBadgeIds`, a cada
   * snapshot de perfil/stats) a pula. É para regra que não decide em cima de
   * contador nenhum — hoje só `madrugador`, que lê o relógio: avaliada de
   * forma passiva, ela pedia a badge para qualquer pessoa que ABRISSE o app de
   * madrugada, sem livro concluído (BUG-261).
   */
  eventOnly?: true;
  /**
   * "Quanto falta?" — só para regra de CONTADOR (páginas, livros, dias,
   * moedas, resenhas, horas). Alimenta a folha de detalhe da `/badges`
   * (BUG-280): o ladrilho bloqueado dizia a condição em 10px cortados em duas
   * linhas, e a pessoa não tinha como saber se estava a 5 ou a 500 páginas da
   * conquista — nem se o app tinha deixado de contar.
   *
   * Nasce SEMPRE por `counterRule`, nunca escrita à mão ao lado do `check`:
   * são o mesmo número, e dois literais divergem em silêncio (o limiar da
   * barra diria "1.000" e o pedido sairia em 900, ou o contrário).
   */
  progress?: (stats: BadgeCheckStats, profile: UserProfile) => BadgeProgress;
}

export type BadgeProgressUnit =
  | "livros"
  | "páginas"
  | "dias seguidos"
  | "Livra Coins"
  | "resenhas"
  | "horas de Foco"
  | "gêneros"
  | "independentes";

export interface BadgeProgress {
  /** Nunca acima de `target` — a barra cheia é o teto, não 130%. */
  current: number;
  target: number;
  unit: BadgeProgressUnit;
}

/**
 * Regra de contador: `check` e `progress` derivados do MESMO `target`.
 *
 * `of` devolve a medida na unidade da barra — para o Foco é HORAS inteiras
 * (`totalFocusMin / 60` truncado), então "9 de 10 horas" só vira 10 quando
 * os 600 minutos fecham, exatamente onde o servidor concede.
 */
function counterRule(
  of: (stats: BadgeCheckStats, profile: UserProfile) => number,
  target: number,
  unit: BadgeProgressUnit,
): Pick<BadgeRule, "check" | "progress"> {
  return {
    check: (stats, profile) => of(stats, profile) >= target,
    progress: (stats, profile) => ({
      current: Math.min(Math.max(0, of(stats, profile)), target),
      target,
      unit,
    }),
  };
}

const coinsOf = (stats: BadgeCheckStats) => stats.wallet?.coins ?? 0;
const reviewsOf = (stats: BadgeCheckStats) => stats.totalReviews ?? 0;
const writtenReviewsOf = (stats: BadgeCheckStats) => stats.totalWrittenReviews ?? 0;
const wantToReadOf = (stats: BadgeCheckStats) => stats.wantToReadCount ?? 0;
const focusHoursOf = (stats: BadgeCheckStats) => Math.floor((stats.totalFocusMin ?? 0) / 60);

/**
 * A régua das badges de ofensiva: o RECORDE, não o estado de hoje (BUG-219).
 *
 * Espelho de `bestStreak` em `functions/src/books/badges.ts`, que é a
 * autoridade — o client só pré-filtra, e um client mais frouxo que o servidor
 * pede badges que a callable recusa em silêncio (é o modo de falha que
 * `badgeThresholdParity.test.ts` existe para pegar).
 *
 * Por que não `currentStreak`: badge é troféu, e `currentStreak >= 7` é status
 * ao vivo. Quem tirava 7 dias seguidos e quebrava a sequência ficava com a
 * conquista bloqueada para sempre, porque o reset acontece na MESMA escrita que
 * acorda este pré-filtro. `longestStreak` é mantido por
 * `Math.max(nextStreak, prevLongest)` nos dois escritores de ofensiva.
 */
export function bestStreakOf(stats: BadgeCheckStats): number {
  return Math.max(stats.currentStreak, stats.longestStreak ?? 0);
}

const BADGE_RULES: BadgeRule[] = [
  {
    id: "primeira_leitura",
    label: "Primeiro Sangue",
    emoji: "📖",
    description: "Você concluiu seu primeiro livro no Livra!",
    publicDescription: "Concluiu o primeiro livro no Livra.",
    rarity: "common",
    ...counterRule((s) => s.totalBooksRead, 1, "livros"),
  },
  {
    id: "leitor_de_fim_de_semana",
    label: "Leitor de Fim de Semana",
    emoji: "☕",
    description: "5 livros concluídos. Bom ritmo!",
    rarity: "rare",
    ...counterRule((s) => s.totalBooksRead, 5, "livros"),
  },
  {
    id: "leitor_assiduo",
    label: "Leitor Assíduo",
    emoji: "🔥",
    description: "10 livros concluídos. Dedicação de verdade!",
    publicDescription: "10 livros concluídos.",
    rarity: "epic",
    ...counterRule((s) => s.totalBooksRead, 10, "livros"),
  },
  {
    id: "meta_cumprida",
    label: "Promessa Cumprida",
    emoji: "🎯",
    description: "Você atingiu sua meta de leitura do ano!",
    publicDescription: "Bateu a meta de leitura que prometeu para o ano.",
    rarity: "epic",
    // ⚠️ `booksReadThisYear`, não `totalBooksRead`. A promessa é de um ANO;
    // medi-la pelo contador vitalício entregava a badge a quem catalogasse
    // uma estante antiga. Espelha o gate server-side, que é a autoridade.
    check: (stats, profile) =>
      profile.readingGoal > 0 &&
      stats.booksReadThisYear !== undefined &&
      stats.booksReadThisYear >= profile.readingGoal,
  },
  {
    id: "cem_paginas",
    label: "Cem Páginas",
    emoji: "📄",
    description: "100 páginas lidas no total.",
    rarity: "common",
    ...counterRule((s) => s.totalPagesRead, 100, "páginas"),
  },
  {
    id: "mil_paginas",
    label: "Mil Páginas",
    emoji: "📚",
    description: "1.000 páginas lidas. Impressionante!",
    rarity: "rare",
    ...counterRule((s) => s.totalPagesRead, 1_000, "páginas"),
  },
  {
    id: "dez_mil_paginas",
    label: "Devorador de Livros",
    emoji: "🐉",
    description: "10.000 páginas lidas. Lendário.",
    rarity: "legendary",
    secret: true,
    ...counterRule((s) => s.totalPagesRead, 10_000, "páginas"),
  },
  {
    id: "ofensiva_7",
    label: "Semana Perfeita",
    emoji: "🔥",
    description: "7 dias seguidos de leitura. Você está em chamas!",
    publicDescription: "7 dias seguidos de leitura.",
    rarity: "rare",
    ...counterRule(bestStreakOf, 7, "dias seguidos"),
  },
  {
    id: "ofensiva_30",
    label: "Mês de Fogo",
    emoji: "🌋",
    description: "30 dias seguidos de leitura. Imparável.",
    rarity: "epic",
    ...counterRule(bestStreakOf, 30, "dias seguidos"),
  },
  {
    id: "ofensiva_60",
    label: "Lendário",
    emoji: "💎",
    description: "60 dias seguidos. Você é uma lenda da leitura.",
    publicDescription: "60 dias seguidos de leitura.",
    rarity: "legendary",
    secret: true,
    ...counterRule(bestStreakOf, 60, "dias seguidos"),
  },

  // ---------------------------------------------------------------------------
  // Pacote de Expansão — Gamificação Fase 4.1
  // ---------------------------------------------------------------------------

  {
    id: "decorador",
    label: "Decorador de Interiores",
    emoji: "🎨",
    description: "Desbloqueou o seu primeiro tema premium na Loja Livra.",
    publicDescription: "Desbloqueou o primeiro tema premium na Loja Livra.",
    rarity: "rare",
    check: (_stats, profile) => {
      const p = profile as typeof profile & { unlockedThemes?: string[] };
      return (p.unlockedThemes?.length ?? 0) >= 3;
    },
  },
  {
    id: "aprendiz_foco",
    label: "Aprendiz do Foco",
    emoji: "🪙",
    description: "Acumulou as suas primeiras 100 Livra Coins.",
    publicDescription: "Acumulou as primeiras 100 Livra Coins.",
    rarity: "common",
    ...counterRule(coinsOf, 100, "Livra Coins"),
  },
  {
    id: "zona_conforto",
    label: "Zona de Conforto",
    emoji: "🔄",
    description: "Completou a releitura de um livro que já tinha lido antes.",
    rarity: "rare",
    secret: true,
    check: (stats) => (stats.totalReads ?? 0) > (stats.uniqueBooksRead ?? 0),
  },
  {
    id: "capitalista",
    label: "Capitalista Literário",
    emoji: "💰",
    description: "Acumulou um tesouro de 1.000 Livra Coins sem gastar.",
    rarity: "epic",
    secret: true,
    ...counterRule(coinsOf, 1_000, "Livra Coins"),
  },

  // ---------------------------------------------------------------------------
  // Easter Eggs — Conquistas Ocultas
  // ---------------------------------------------------------------------------

  // `numero_da_besta` ("Completou exatamente 6 livros", 😈) foi APOSENTADA em
  // 05/08 por decisão do fundador. Sai do catálogo dos dois lados — aqui e em
  // `functions/src/shared/badgeCatalog.ts` + o `case` de
  // `checkBadgeCondition` —, então ninguém mais a ganha e o servidor recusa se
  // alguém pedir.
  //
  // ⚠️ NÃO houve limpeza de dado: quem já a tinha continua com o id em
  // `users.badges`. Ele deixa de renderizar em toda superfície porque todas
  // resolvem por `BADGE_CATALOG` e descartam o desconhecido (a de Conquistas do
  // perfil ganhou o filtro explícito na mesma fatia). Badge é one-way por
  // desenho e não existe caminho de revogação; apagar o id à mão exigiria
  // varrer a base, e o efeito visível seria o mesmo.
  {
    id: "mestre_do_foco",
    label: "Mestre do Foco",
    emoji: "🧠",
    description: "Acumulou 500 Livra Coins através do Modo Foco.",
    rarity: "rare",
    secret: true,
    ...counterRule(coinsOf, 500, "Livra Coins"),
  },
  {
    id: "madrugador",
    label: "Coruja Noturna",
    emoji: "🦉",
    description: "Terminou a leitura de um livro entre meia-noite e as 5 da manhã.",
    rarity: "rare",
    secret: true,
    // Avaliado no momento exato em que checkAndUnlockBadges é chamado (conclusão
    // do livro) — e SÓ nele: `eventOnly` tira esta regra do gatilho passivo,
    // que desde o BUG-219 roda a cada snapshot e pedia a badge para quem
    // abrisse o app de madrugada (BUG-261). Hora LOCAL do aparelho de
    // propósito: "madrugada" é a da pessoa; o servidor confere em hora BR.
    eventOnly: true,
    check: () => {
      const hour = new Date().getHours();
      return hour >= 0 && hour <= 5;
    },
  },

  // ---------------------------------------------------------------------------
  // Leva de 31/08 — as 8 client-pedíveis das 13 novas (§7.3)
  //
  // Todas decidem em cima de `user_stats`/`users`, que é o que o hook já tem em
  // memória. As outras 5 (4 de liga + `foco_maratona`) NÃO entram aqui: elas
  // são concedidas pelo servidor no instante do evento e vivem em
  // `SERVER_GRANTED_BADGES`, sem `check`.
  // ---------------------------------------------------------------------------

  {
    id: "cinquenta_livros",
    label: "Meia Centena",
    emoji: "\u{1F4DA}",
    description: "50 livros concluídos.",
    rarity: "legendary",
    // O degrau que faltava: a escada de volume parava em 10 (`leitor_assiduo`).
    ...counterRule((s) => s.totalBooksRead, 50, "livros"),
  },
  {
    id: "ofensiva_100",
    label: "Chama Eterna",
    emoji: "\u{1F56F}\uFE0F",
    description: "100 dias seguidos de leitura. A chama não apaga mais.",
    rarity: "legendary",
    secret: true,
    ...counterRule(bestStreakOf, 100, "dias seguidos"),
  },
  {
    id: "meta_dobrada",
    label: "Meta Dobrada",
    emoji: "\u{1F3AF}",
    description: "Leu o DOBRO da meta que prometeu para o ano.",
    rarity: "epic",
    // Mesma fonte de `meta_cumprida` — do ANO, nunca o contador vitalício —,
    // então nasce sem custo nenhum: o dado já é apurado para a badge irmã.
    check: (stats, profile) =>
      profile.readingGoal > 0 &&
      stats.booksReadThisYear !== undefined &&
      stats.booksReadThisYear >= profile.readingGoal * 2,
  },
  {
    id: "primeira_resenha",
    label: "Primeira Resenha",
    emoji: "\u270D\uFE0F",
    description: "Escreveu sua primeira resenha com texto.",
    publicDescription: "Escreveu a primeira resenha com texto.",
    rarity: "common",
    // Com TEXTO desde 24/09 (espelho do `case` do servidor).
    ...counterRule(writtenReviewsOf, 1, "resenhas"),
  },
  {
    id: "resenhista",
    label: "Resenhista",
    emoji: "\u{1F4DD}",
    description: "25 resenhas escritas. Sua opinião virou hábito.",
    publicDescription: "25 resenhas escritas. Opinar virou hábito.",
    rarity: "rare",
    ...counterRule(reviewsOf, 25, "resenhas"),
  },
  {
    id: "critico_publico",
    label: "Crítico Público",
    emoji: "\u{1F4E3}",
    description: "50 resenhas escritas. Gente decide o que ler por sua causa.",
    publicDescription: "50 resenhas escritas. Tem gente decidindo o que ler por causa delas.",
    rarity: "epic",
    ...counterRule(reviewsOf, 50, "resenhas"),
  },
  {
    id: "foco_dez_horas",
    label: "Dez Horas de Foco",
    emoji: "\u231B",
    description: "10 horas acumuladas no Modo Foco.",
    rarity: "rare",
    ...counterRule(focusHoursOf, 10, "horas de Foco"),
  },
  {
    id: "foco_cem_horas",
    label: "Cem Horas de Foco",
    emoji: "\u231B",
    description: "100 horas acumuladas no Modo Foco. Isso é uma estação inteira.",
    rarity: "legendary",
    secret: true,
    ...counterRule(focusHoursOf, 100, "horas de Foco"),
  },

  // ── Difíceis de 24/09 (BACKLOG §0 item 11 F2) — os mesmos contadores, o
  // degrau lendário. Espelho dos `case` de `functions/src/shared/badgeConditions.ts`.
  {
    id: "ofensiva_365",
    label: "Um Ano Inteiro",
    emoji: "☀️",
    description: "365 dias seguidos de leitura. Uma volta inteira ao redor do sol.",
    rarity: "legendary",
    ...counterRule(bestStreakOf, 365, "dias seguidos"),
  },
  {
    id: "cem_livros",
    label: "Centena",
    emoji: "\u{1F3DB}️",
    description: "100 livros concluídos.",
    rarity: "legendary",
    ...counterRule((s) => s.totalBooksRead, 100, "livros"),
  },
  {
    id: "cinquenta_mil_paginas",
    label: "Cinquenta Mil",
    emoji: "\u{1F3D4}️",
    description: "50.000 páginas lidas.",
    rarity: "legendary",
    ...counterRule((s) => s.totalPagesRead, 50_000, "páginas"),
  },
  {
    id: "monge_foco",
    label: "Monge",
    emoji: "\u{1FAB7}",
    description: "500 horas acumuladas no Modo Foco.",
    rarity: "legendary",
    ...counterRule(focusHoursOf, 500, "horas de Foco"),
  },
  {
    id: "critico_carreira",
    label: "Crítico de Carreira",
    emoji: "\u{1F58B}️",
    description: "100 resenhas com texto. Isso já é uma coluna.",
    rarity: "legendary",
    ...counterRule(writtenReviewsOf, 100, "resenhas"),
  },
  {
    id: "tsundoku",
    label: "Tsundoku",
    emoji: "\u{1F5FC}",
    description: "100 livros em Quero ler ao mesmo tempo.",
    rarity: "epic",
    secret: true,
    ...counterRule(wantToReadOf, 100, "livros"),
  },
  {
    id: "um_por_semana",
    label: "Um por Semana",
    emoji: "\u{1F5D3}️",
    description: "52 livros concluídos no mesmo ano.",
    rarity: "legendary",
    // Sem barra de progresso, pelo mesmo motivo de `meta_cumprida` (ver
    // `getBadgeProgress`): `booksReadThisYear` só é apurado na conclusão.
    check: (stats) => stats.booksReadThisYear !== undefined && stats.booksReadThisYear >= 52,
  },
];

/**
 * Badges que o SERVIDOR concede — o client nunca as tenta destravar.
 *
 * Lista separada de `BADGE_RULES` de propósito: aquela é "o que o client pode
 * pedir", e toda entrada dela tem um `check`. Uma badge de liga aqui com
 * `check: () => false` funcionaria, mas convidaria alguém a escrever a
 * condição depois — e a condição não existe no client (quem decide é o
 * fechamento da sala, no domingo). Sem `check`, o erro é inexprimível.
 *
 * ⚠️ Espelho de `functions/src/shared/badgeCatalog.ts`. As duas listas são
 * comparadas por `tests/unit/leagueBadgeCatalog.test.ts` nos dois sentidos —
 * badge que existe só num lado é badge que alguém ganha e não vê, que foi
 * exatamente o BUG-87 com o flair Creator.
 */
const SERVER_GRANTED_BADGES: BadgeDefinition[] = [
  // ── Secretas de calendário (24/09, BACKLOG §0 item 11 F1) — o SERVIDOR concede
  // no registro de leitura (`functions/src/shared/dateBadges.ts`); o client só
  // desenha. Ocultas até alguém ganhar.
  { id: "sexta_treze",      label: "Sexta-feira 13",   emoji: "🐈‍⬛", description: "Leu numa sexta-feira 13. Superstição nenhuma segura um bom livro.", rarity: "rare", secret: true },
  { id: "dia_do_livro",     label: "Dia do Livro",     emoji: "📚", description: "Leu no Dia Nacional do Livro, 29 de outubro.", rarity: "common", secret: true },
  // Secretas de estante (24/09, item 11 F4) — `functions/src/books/shelfBadges.ts`
  // + Fênix no registro de leitura.
  { id: "guia_mochileiro",  label: "Não Entre em Pânico", emoji: "🌌", description: "Pôs O Guia do Mochileiro das Galáxias na estante. Leve uma toalha.", rarity: "common", secret: true },
  { id: "pequeno_principe", label: "Cativado",           emoji: "🌹", description: "Pôs O Pequeno Príncipe na estante.", rarity: "common", secret: true },
  { id: "dom_casmurro",     label: "Olhos de Ressaca",   emoji: "🌊", description: "Terminou Dom Casmurro. Traiu ou não traiu?", rarity: "rare", secret: true },
  { id: "bip",              label: "Bip!",               emoji: "📷", description: "Pôs um livro na estante pelo código de barras.", rarity: "common", secret: true },
  { id: "fenix",            label: "Fênix",              emoji: "🐦‍🔥", description: "Voltou a ler depois de 30 dias ou mais sem registro.", rarity: "rare", secret: true },
  { id: "sem_ressaca",      label: "Sem Ressaca",        emoji: "☕", description: "Começou um livro novo no mesmo dia em que terminou outro.", rarity: "common", secret: true },
  { id: "virada_literaria", label: "Virada Literária", emoji: "🎆", description: "Estava lendo quando o ano virou.", rarity: "rare", secret: true },
  // Livro Secreto do Mês (item 12b F5) — escolhido no /admin, concedido no
  // create de `user_books` (`functions/src/books/secretBookOfMonth.ts`).
  { id: "livro_secreto_mes", label: "Livro Secreto do Mês", emoji: "🗝️", description: "Pôs na estante o livro secreto do mês. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  // Secretas mensais (06/10, BACKLOG §13) — mock `redesign-livra/secretas-mensais`;
  // o servidor só concede a partir do mês da pista (`shared/secretActivation.ts`).
  { id: "primeira_pessoa", label: "Primeira Pessoa", emoji: "📄", description: "Terminou Verity.", rarity: "common", secret: true },
  { id: "hipotese_confirmada", label: "Hipótese Confirmada", emoji: "🧪", description: "Terminou A Hipótese do Amor.", rarity: "common", secret: true },
  { id: "laboratorio_hazelwood", label: "Laboratório Hazelwood", emoji: "🔬", description: "Terminou 3 livros da Ali Hazelwood.", rarity: "rare", secret: true },
  { id: "comeco_e_fim", label: "Começo e Fim", emoji: "♾️", description: "Terminou É Assim Que Acaba e É Assim Que Começa.", rarity: "rare", secret: true },
  { id: "reserva_confirmada", label: "Reserva Confirmada", emoji: "🍽️", description: "Terminou Jantar Secreto.", rarity: "common", secret: true },
  { id: "alem_da_muralha", label: "Além da Muralha", emoji: "🗡️", description: "Terminou Corte de Espinhos e Rosas.", rarity: "common", secret: true },
  { id: "sobrevivente_parapeito", label: "Sobrevivente do Parapeito", emoji: "🪶", description: "Terminou Quarta Asa.", rarity: "common", secret: true },
  { id: "setimo_marido", label: "Sétimo Marido", emoji: "💚", description: "Terminou Os Sete Maridos de Evelyn Hugo.", rarity: "common", secret: true },
  { id: "meia_noite", label: "Meia-Noite", emoji: "🕛", description: "Terminou A Biblioteca da Meia-Noite.", rarity: "common", secret: true },
  { id: "nao_confie", label: "Não Confie em Ninguém", emoji: "🔒", description: "Terminou um thriller da Freida McFadden.", rarity: "common", secret: true },
  { id: "semideus", label: "Semideus", emoji: "⚡", description: "Terminou O Ladrão de Raios.", rarity: "common", secret: true },
  { id: "sonhador", label: "Sonhador", emoji: "🌙", description: "Terminou Noites Brancas.", rarity: "rare", secret: true },
  { id: "defunto_autor", label: "Defunto Autor", emoji: "📜", description: "Terminou Memórias Póstumas de Brás Cubas.", rarity: "rare", secret: true },
  { id: "bloco_eu_sozinho", label: "Bloco do Eu Sozinho", emoji: "🎭", description: "Leu no Carnaval.", rarity: "common", secret: true },
  { id: "baleia", label: "Baleia", emoji: "🐾", description: "Terminou Vidas Secas.", rarity: "rare", secret: true },
  { id: "pirlimpimpim", label: "Pirlimpimpim", emoji: "✨", description: "Leu no Dia do Livro Infantil, 18 de abril.", rarity: "common", secret: true },
  { id: "grande_irmao", label: "Grande Irmão", emoji: "👁️", description: "Pôs 1984 na estante.", rarity: "common", secret: true },
  { id: "anarrie", label: "Anarriê", emoji: "🎏", description: "Leu no dia de São João, 24 de junho.", rarity: "common", secret: true },
  { id: "tinta_na_mao", label: "Tinta na Mão", emoji: "✒️", description: "Escreveu uma resenha com texto no Dia do Escritor, 25 de julho.", rarity: "rare", secret: true },
  { id: "moinhos", label: "Moinhos", emoji: "🌬️", description: "Terminou Dom Quixote.", rarity: "rare", secret: true },
  { id: "livro_secreto_2026_11", label: "Chave de Novembro", emoji: "🗝️", description: "Achou o Livro Secreto de novembro de 2026. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2026_12", label: "Chave de Dezembro", emoji: "🗝️", description: "Achou o Livro Secreto de dezembro de 2026. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_01", label: "Chave de Janeiro", emoji: "🗝️", description: "Achou o Livro Secreto de janeiro de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_02", label: "Chave de Fevereiro", emoji: "🗝️", description: "Achou o Livro Secreto de fevereiro de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_03", label: "Chave de Março", emoji: "🗝️", description: "Achou o Livro Secreto de março de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_04", label: "Chave de Abril", emoji: "🗝️", description: "Achou o Livro Secreto de abril de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_05", label: "Chave de Maio", emoji: "🗝️", description: "Achou o Livro Secreto de maio de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_06", label: "Chave de Junho", emoji: "🗝️", description: "Achou o Livro Secreto de junho de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_07", label: "Chave de Julho", emoji: "🗝️", description: "Achou o Livro Secreto de julho de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_08", label: "Chave de Agosto", emoji: "🗝️", description: "Achou o Livro Secreto de agosto de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_09", label: "Chave de Setembro", emoji: "🗝️", description: "Achou o Livro Secreto de setembro de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  { id: "livro_secreto_2027_10", label: "Chave de Outubro", emoji: "🗝️", description: "Achou o Livro Secreto de outubro de 2027. A pista estava nas redes da Livra.", rarity: "rare", secret: true },
  // Tema de evento (15/10/2026): equipar o Halloween dentro da janela. NÃO é
  // secreta — durante o evento aparece apagada; depois, só para quem ganhou
  // (`utils/eventBadges.ts`).
  { id: "halloween_2026", label: "Gostosuras ou Leituras", emoji: "🎃", description: "Vestiu o Livra de Halloween em 2026.", rarity: "common" },
  // Difíceis por EVENTO (item 12b F6) — conclusão de livro e fechamento de
  // temporada. Visíveis desde o início (não são easter egg).
  { id: "tijolao",            label: "Tijolão",            emoji: "🧱", description: "Terminou um livro de 800 páginas ou mais.", rarity: "epic" },
  { id: "maratona_tijolo",    label: "Maratona de Tijolo", emoji: "⏱️", description: "Terminou um livro de 500+ páginas em até 7 dias depois de começar.", rarity: "legendary" },
  { id: "ecletico",           label: "Eclético",           emoji: "🎨", description: "Terminou livros de 10 gêneros diferentes.", rarity: "epic" },
  // Garimpo (21/10/2026, docs/product/livra-autores.md §15 D47, mock
  // `redesign-livra/autores/a4/`): obras independentes concluídas, contadas
  // pelo servidor na conclusão (`books/completionBadges.ts`). Textos do mock.
  { id: "garimpo_1",  label: "Primeiro Garimpo", emoji: "⛏️", description: "Você terminou seu primeiro livro independente.", publicDescription: "Terminou o primeiro livro independente.", rarity: "common" },
  { id: "garimpo_5",  label: "Faro de Garimpo",  emoji: "⛏️", description: "5 livros independentes concluídos. Você já sabe onde procurar.", publicDescription: "5 livros independentes concluídos. Sabe onde procurar.", rarity: "rare" },
  { id: "garimpo_10", label: "Garimpo de Ouro",  emoji: "⛏️", description: "10 livros independentes concluídos. Você chega antes das vitrines.", publicDescription: "10 livros independentes concluídos. Chega antes das vitrines.", rarity: "epic" },
  { id: "temporada_perfeita", label: "Temporada Perfeita", emoji: "👑", description: "Fechou as 8 semanas de uma temporada no Diamante.", rarity: "legendary" },
  // Platina (item 12b F7) — o servidor concede ao cruzar as 45.
  { id: "platina",            label: "Livra Platinado",    emoji: "💠", description: "Todas as 45 conquistas de leitura e hábito.", rarity: "legendary" },

  { id: "liga_bronze_topo",   label: "Topo do Bronze",        emoji: "🥉", description: "Terminou a semana no pódio da Liga de Bronze.", rarity: "common" },
  { id: "liga_silver_topo",   label: "Topo da Prata",         emoji: "🥈", description: "Terminou a semana no pódio da Liga de Prata.", rarity: "rare" },
  { id: "liga_gold_topo",     label: "Topo do Ouro",          emoji: "🥇", description: "Terminou a semana no pódio da Liga de Ouro.", rarity: "epic" },
  { id: "liga_diamond_podio", label: "Pódio de Diamante",     emoji: "💎", description: "Terminou a semana no pódio da Liga de Diamante — o teto do jogo.", rarity: "epic" },
  { id: "imperador_diamante", label: "Imperador do Diamante", emoji: "👑", description: "Primeiro lugar na Liga de Diamante. Não há acima disso.", rarity: "legendary" },

  // ── Leva de 31/08: as 5 concedidas pelo servidor entre as 13 novas ────────
  //
  // As quatro de liga saem do agregado `users/{uid}/league_summary/current`, no
  // fechamento da semana — o client não tem a sequência de semanas em lugar
  // nenhum. `foco_maratona` sai de `completeFocusSession`: só o servidor sabe
  // quanto durou UMA sessão, e usar o relógio do client aqui seria oferecer a
  // conquista a quem deixa o cronômetro correndo.
  { id: "liga_veterano",   label: "Veterano da Liga", emoji: "🗓️", description: "Quatro semanas seguidas pontuando na liga.", rarity: "rare" },
  { id: "liga_inabalavel", label: "Inabalável",       emoji: "💎", description: "Três semanas seguidas fechando na Liga de Diamante.", rarity: "epic" },
  { id: "liga_ascensao",   label: "Em Ascensão",      emoji: "🚀", description: "Subiu de liga três vezes.", rarity: "rare" },
  { id: "liga_centuriao",  label: "Centurião",        emoji: "🏛️", description: "Cem semanas de liga. Você viu o jogo inteiro.", publicDescription: "Cem semanas de liga. Viu o jogo inteiro.", rarity: "legendary" },
  { id: "foco_maratona",   label: "Maratona de Foco", emoji: "⏱️", description: "Uma única sessão de foco de duas horas.", rarity: "epic" },

  // ── Temporadas (13/09) — todas do servidor, no `closeWeeklyLeagues` ────────
  // Espelho de `functions/src/shared/badgeCatalog.ts`. Pico de tier a cada
  // virada em que o pico sobe; estrelas e prêmios de escada só no fechamento.
  { id: "temporada_pico_bronze",       label: "Temporada no Bronze",   emoji: "🥉", description: "Fechou uma semana de temporada no Bronze.", rarity: "common" },
  { id: "temporada_pico_silver",       label: "Temporada na Prata",    emoji: "🥈", description: "Chegou à Prata dentro de uma temporada.", rarity: "rare" },
  { id: "temporada_pico_gold",         label: "Temporada no Ouro",     emoji: "🥇", description: "Chegou ao Ouro dentro de uma temporada.", rarity: "epic" },
  { id: "temporada_pico_diamond",      label: "Temporada no Diamante", emoji: "💎", description: "Chegou ao Diamante dentro de uma temporada.", rarity: "legendary" },
  { id: "temporada_estrela_leitor",    label: "Quem Mais Leu",         emoji: "📚", description: "O maior XP acumulado de uma temporada inteira.", rarity: "legendary" },
  { id: "temporada_estrela_constante", label: "Mais Constante",        emoji: "🗓️", description: "Pontuou em mais semanas da temporada do que qualquer outra pessoa.", rarity: "epic" },
  { id: "temporada_estrela_diamante",  label: "Morador do Diamante",   emoji: "💎", description: "Quem mais semanas fechou no Diamante numa temporada.", rarity: "legendary" },
  { id: "temporada_estrela_semana",    label: "Maior Semana",          emoji: "⚡", description: "A maior semana de XP de toda a temporada.", rarity: "epic" },
  { id: "temporada_estrela_foco",      label: "Campeão do Foco",       emoji: "⏱️", description: "Mais minutos de Foco numa temporada.", rarity: "epic" },
  // Por temporada (carregam cosmético via `requiresBadge`, ver `seasonAwardBadgeIds`).
  { id: "temporada_2026t1_top10",      label: "Top 10 — Temporada 1",  emoji: "🏆", description: "Entre os 10 primeiros da escada da Temporada 1 (Eclipse, out–nov/2026).", rarity: "epic" },
  { id: "temporada_2026t1_campeao",    label: "Campeão — Temporada 1", emoji: "👑", description: "Primeiro lugar da escada da Temporada 1 (Eclipse, out–nov/2026).", rarity: "legendary" },
  { id: "temporada_2026t1_meta",       label: "Constante — Temporada 1", emoji: "📅", description: "Leu em 40 dias diferentes da Temporada 1 (Eclipse, out–nov/2026).", rarity: "epic" },
  { id: "temporada_2026t1_coletiva",   label: "Eclipse coletivo — Temporada 1", emoji: "🌘", description: "A comunidade bateu a meta de páginas da Temporada 1 — e você pontuou nela.", publicDescription: "A comunidade bateu a meta de páginas da Temporada 1, com pontos desta leitura.", rarity: "epic" },
  { id: "temporada_2026t2_top10",      label: "Top 10 — Temporada 2",  emoji: "🏆", description: "Entre os 10 primeiros da escada da Temporada 2 (Entardecer, dez/2026–jan/2027).", rarity: "epic" },
  { id: "temporada_2026t2_campeao",    label: "Campeão — Temporada 2", emoji: "👑", description: "Primeiro lugar da escada da Temporada 2 (Entardecer, dez/2026–jan/2027).", rarity: "legendary" },
  { id: "temporada_2026t2_meta",       label: "Constante — Temporada 2", emoji: "📅", description: "Leu em 40 dias diferentes da Temporada 2 (Entardecer, dez/2026–jan/2027).", rarity: "epic" },
  { id: "temporada_2026t2_coletiva",   label: "Céu de verão coletivo — Temporada 2", emoji: "🌠", description: "A comunidade bateu a meta de páginas da Temporada 2 — e você pontuou nela.", publicDescription: "A comunidade bateu a meta de páginas da Temporada 2, com pontos desta leitura.", rarity: "epic" },
];

const SERVER_GRANTED_BADGE_IDS: ReadonlySet<string> = new Set(SERVER_GRANTED_BADGES.map((b) => b.id));

/** Badge que o SERVIDOR concede (sem `check` no client). Não diz quem a anuncia. */
export function isServerGrantedBadge(id: string): boolean {
  return SERVER_GRANTED_BADGE_IDS.has(id);
}

/**
 * Badge com ANUNCIANTE PRÓPRIO: liga/temporada saem no cartão de fechamento da
 * semana/temporada (`lastClosedBadges`), e `foco_maratona` fica com elas desde
 * o BUG-281. O watcher de anúncio usa isto para não celebrá-las de novo.
 *
 * ⚠️ NÃO é "concedida pelo servidor" (BUG-303): as secretas, as difíceis por
 * evento e a Platina também são do servidor e NÃO têm outro anunciante — com
 * `isServerGrantedBadge` no lugar disto, chegavam mudas desde 24/09.
 */
export function hasOwnBadgeAnnouncer(id: string): boolean {
  return (
    id.startsWith("liga_") ||
    id === "imperador_diamante" ||
    id.startsWith("temporada_") ||
    id === "foco_maratona"
  );
}

// Mapa para lookup rápido por ID.
// Usa spread para preservar todos os campos de BadgeDefinition (incluindo `secret`).
//
// ⚠️ Inclui as concedidas pelo servidor: este mapa é o que TODA superfície de
// render consulta (`BadgeIcon`, `BadgePicker`, a seção Conquistas do perfil,
// o cartão de unlock), e id ausente aqui é descartado em silêncio pelos
// `filter(Boolean)` delas. É também o denominador do "N de M conquistas".
export const BADGE_CATALOG: Record<string, BadgeDefinition> = Object.fromEntries(
  [
    ...BADGE_RULES.map(({ check: _check, progress: _progress, ...def }) => def),
    ...SERVER_GRANTED_BADGES,
  ].map((def) => [def.id, BADGE_LORE[def.id] ? { ...def, lore: BADGE_LORE[def.id] } : def])
);

/**
 * O texto da badge para quem VISITA o perfil — ver `publicDescription`. Secreta
 * com frase simbólica mostra a frase: a receita revelada acabaria com o
 * easter egg de quem ainda não o achou (06/10).
 */
export function badgePublicDescription(badge: BadgeDefinition): string {
  return badge.lore ?? badge.publicDescription ?? badge.description;
}

/** Texto principal nas superfícies do DONO e no card compartilhado. */
export function badgeShowcaseText(badge: BadgeDefinition): string {
  return badge.lore ?? badge.description;
}

/** "Como você ganhou" — só existe quando a frase simbólica tomou o lugar da receita. */
export function badgeHowEarned(badge: BadgeDefinition): string | null {
  return badge.lore ? badge.description : null;
}

// ---------------------------------------------------------------------------
// Overlay do painel (Bloco G, Admin-Alavancas) — rótulo/emoji/descrição/
// raridade/visibilidade por cima do catálogo de código, sem build.
// docs/systems/admin-badge-quest-meta.md §3.
// ---------------------------------------------------------------------------

interface BadgeMetaOverlayDoc {
  label?: string;
  emoji?: string;
  description?: string;
  rarity?: string; // validado contra o union antes de aplicar — ver abaixo
  hidden?: boolean;
}

const BADGE_RARITY_VALUES = new Set<BadgeRarity>(["common", "rare", "epic", "legendary"]);

/**
 * `BADGE_CATALOG` não é mais imutável-por-construção: os objetos que ela
 * indexa são reescritos IN-PLACE aqui, sem trocar a identidade do Record
 * nem das entradas — quem lê hoje faz `BADGE_CATALOG[id]`, nunca desestrutura
 * o mapa inteiro, então a mutação in-place é segura para esse padrão de
 * leitura (blueprint §3.1).
 *
 * ⚠️ `...WhenAuthed`: `badge_meta` é `allow read: if isAuthed()`, e este
 * arquivo é importado por `AppRoutes` (via `useBadgeAutoUnlock`) em TODO boot,
 * inclusive deslogado. Assinar no import fazia o listener morrer em
 * `permission-denied` na landing/login — ver o docblock de `metaOverlay.ts`.
 */
watchMetaOverlayWhenAuthed<BadgeMetaOverlayDoc>("badge_meta", (overlay) => {
  for (const [id, meta] of overlay) {
    const base = BADGE_CATALOG[id];
    if (!base) continue; // id órfão no Firestore — servidor recusa gravar; defesa extra
    if (typeof meta.label === "string") base.label = meta.label;
    if (typeof meta.emoji === "string") base.emoji = meta.emoji;
    if (typeof meta.description === "string") {
      base.description = meta.description;
      // O painel reescreveu a condição: a versão pública do código descreveria
      // a condição ANTIGA. Vale o texto do painel nas duas superfícies.
      delete base.publicDescription;
    }
    // Um valor corrompido gravado por engano (ex.: edição direta no console
    // do Firestore) não pode quebrar o render, que indexa `RARITY_TINTS` sem
    // fallback em algumas superfícies.
    if (typeof meta.rarity === "string" && BADGE_RARITY_VALUES.has(meta.rarity as BadgeRarity)) {
      base.rarity = meta.rarity as BadgeRarity;
    }
    if (typeof meta.hidden === "boolean") base.secret = meta.hidden;
  }
});

/**
 * Filtra badges elegíveis client-side antes de chamar a CF.
 * A CF re-valida server-side; isso aqui só evita disparar dezenas de
 * requests inúteis (uma por badge não desbloqueada).
 *
 * Regras `eventOnly` NÃO entram: este é o caminho passivo (a cada snapshot),
 * e uma regra que não lê contador nenhum seria verdadeira aqui sem evento
 * algum ter acontecido (BUG-261).
 */
export function getEligibleBadgeIds(
  stats: BadgeCheckStats,
  profile: UserProfile,
  alreadyUnlocked: Iterable<string>,
): string[] {
  const existing = new Set<string>(alreadyUnlocked);
  return BADGE_RULES
    .filter((r) => !r.eventOnly && !existing.has(r.id) && r.check(stats, profile))
    .map((r) => r.id);
}

/**
 * "Quanto falta para esta conquista?" — `null` quando a badge não é de
 * contador (liga, temporada, meta do ano, releitura, relógio) ou não existe.
 *
 * Só lê `user_stats`/`users`, que a `/badges` já escuta: custa zero request.
 * A meta do ano (`meta_cumprida`/`meta_dobrada`) fica de fora de propósito —
 * `booksReadThisYear` só é apurado na conclusão de um livro, e uma barra que
 * dissesse "0 de 12" para quem leu 12 seria pior que nenhuma barra.
 */
/**
 * Barra de badge SERVIDOR-concedida (sem `check`, fora do `BADGE_RULES` — o
 * client não pode pedi-la). Só o que tem contador legível pelo client; o
 * limiar é o do servidor (`ECLETICO_MIN_GENRES`, paridade em
 * `completionBadges.test.ts`).
 */
export const ECLETICO_TARGET = 10;
/**
 * Limiares do Garimpo — os do servidor (`GARIMPO_TIERS`, paridade em
 * `garimpo.test.ts`). Unidade "independentes" (mock aprovado 21/10: é a que
 * cabe no "Quase lá" sem cortar "Faro de Garimpo" a 390 px).
 */
export const GARIMPO_TARGETS: Readonly<Record<string, number>> = { garimpo_1: 1, garimpo_5: 5, garimpo_10: 10 };
const garimpoProgress = (target: number) => (stats: BadgeCheckStats): BadgeProgress => ({
  current: Math.min(Math.max(0, stats.indieWorksReadCount ?? 0), target),
  target,
  unit: "independentes",
});
const SERVER_BADGE_PROGRESS: Record<string, (stats: BadgeCheckStats) => BadgeProgress> = {
  ecletico: (stats) => ({
    current: Math.min(Math.max(0, stats.genresReadCount ?? 0), ECLETICO_TARGET),
    target: ECLETICO_TARGET,
    unit: "gêneros",
  }),
  ...Object.fromEntries(Object.entries(GARIMPO_TARGETS).map(([id, n]) => [id, garimpoProgress(n)])),
};

export function getBadgeProgress(
  badgeId: string,
  stats: BadgeCheckStats,
  profile: UserProfile,
): BadgeProgress | null {
  const rule = BADGE_RULES.find((r) => r.id === badgeId);
  if (rule?.progress) return rule.progress(stats, profile);
  return SERVER_BADGE_PROGRESS[badgeId]?.(stats) ?? null;
}

// ---------------------------------------------------------------------------
// checkAndUnlockBadges
// ---------------------------------------------------------------------------

/**
 * Verifica quais badges o usuário desbloqueou com base nos stats recebidos
 * diretamente do contexto (sem leitura do Firestore) e persiste as novas.
 */
export async function checkAndUnlockBadges(
  _userId: string,
  stats: BadgeCheckStats,
  existingBadges: string[],
  readingGoal: number,
): Promise<string[]> {
  const existingSet = new Set<string>(existingBadges);
  const fakeProfile = { stats, readingGoal } as UserProfile;

  const newBadgeIds = BADGE_RULES
    .filter((rule) => !existingSet.has(rule.id) && rule.check(stats, fakeProfile))
    .map((rule) => rule.id);

  if (newBadgeIds.length > 0) {
    const functions = getFunctions(getApp(), FUNCTIONS_REGION);
    const unlockBadge = httpsCallable<{badgeId: string}, {unlocked: boolean}>(
      functions,
      "unlockBadge",
    );

    // Quem celebra a PRIMEIRA é o `BookCompletionModal` — é a única que ele
    // renderiza (`CompletionRatingBlock`, `newBadges[0]`). Reivindicá-la aqui
    // impede que o `useBadgeAutoUnlock`, que roda logo em seguida porque as
    // stats acabaram de mudar, abra um segundo cartão para o mesmo badge.
    markBadgesCelebrated([newBadgeIds[0]]);

    const results = await Promise.allSettled(
      newBadgeIds.map((badgeId) =>
        unlockBadge({badgeId}).then((res) => (res.data?.unlocked ? badgeId : null)),
      ),
    );

    // O resto vai para a fila e aparece quando o modal de conclusão fechar.
    //
    // ⚠️ Delta consciente ao mock (decisão do fundador, 03/08). O mock manda a
    // fila ignorar o fluxo de conclusão inteiro; só que concluir um livro que
    // concede 3 badges mostrava 1 e engolia 2 — que é o próprio bug em
    // correção. O invariante que importa ("nunca dois modais para o MESMO
    // badge") continua de pé, porque a primeira está marcada acima.
    const extras = results
      .map((r) => (r.status === "fulfilled" ? r.value : null))
      .filter((id): id is string => !!id && id !== newBadgeIds[0]);

    if (extras.length > 0) enqueueBadgeUnlocks(extras);
  }

  return newBadgeIds;
}
